// Application Settings Management
// Consolidates all settings: Inventory, Purchase, Approval, General

export type DecimalRoundingMode = 'up' | 'down' | 'nearest' | 'none';
export type StockViewPreference = 'base' | 'purchase' | 'smart';

// ==================== INVENTORY SETTINGS ====================

export interface InventorySettings {
  decimalDigits: number;
  decimalRounding: DecimalRoundingMode;
  allowNegativeStock: boolean;
  minimumStockAlertPercent: number;
  defaultStockView: StockViewPreference;
  autoSuggestConversion: boolean;
  showStockValue: boolean;
}

const DEFAULT_INVENTORY_SETTINGS: InventorySettings = {
  decimalDigits: 2,
  decimalRounding: 'nearest',
  allowNegativeStock: false,
  minimumStockAlertPercent: 20,
  defaultStockView: 'smart',
  autoSuggestConversion: true,
  showStockValue: true,
};

export const DECIMAL_DIGIT_OPTIONS = [
  { value: 0, label: '0 digit (Integer)' },
  { value: 1, label: '1 digit' },
  { value: 2, label: '2 digits' },
  { value: 3, label: '3 digits' },
];

export const ROUNDING_OPTIONS: { value: DecimalRoundingMode; label: string; description: string }[] = [
  { value: 'nearest', label: 'Round Nearest', description: '0.5 → 1, 0.4 → 0' },
  { value: 'up', label: 'Round Up', description: 'Always round up (0.1 → 1)' },
  { value: 'down', label: 'Round Down', description: 'Always round down (0.9 → 0)' },
  { value: 'none', label: 'No Rounding', description: 'Keep exact value' },
];

export const STOCK_VIEW_OPTIONS: { value: StockViewPreference; label: string }[] = [
  { value: 'base', label: 'Base Unit' },
  { value: 'purchase', label: 'Purchase Unit' },
  { value: 'smart', label: 'Smart Display' },
];

export const PERCENTAGE_OPTIONS = [
  { value: 10, label: '10%' },
  { value: 20, label: '20%' },
  { value: 25, label: '25%' },
  { value: 30, label: '30%' },
  { value: 50, label: '50%' },
];

// ==================== PURCHASE SETTINGS ====================

export interface PurchaseSettings {
  defaultPaymentTerms: 'net7' | 'net14' | 'net30' | 'net60' | 'net90' | 'immediate';
  defaultDeliveryDays: number;
  allowPartialReceiving: boolean;
  requireQCBeforeReceiving: boolean;
  autoCreatePOFromApprovedReq: boolean;
}

const DEFAULT_PURCHASE_SETTINGS: PurchaseSettings = {
  defaultPaymentTerms: 'net30',
  defaultDeliveryDays: 7,
  allowPartialReceiving: true,
  requireQCBeforeReceiving: false,
  autoCreatePOFromApprovedReq: false,
};

export const PAYMENT_TERMS_OPTIONS = [
  { value: 'immediate', label: 'Immediate (Cash)' },
  { value: 'net7', label: 'Net 7 days' },
  { value: 'net14', label: 'Net 14 days' },
  { value: 'net30', label: 'Net 30 days' },
  { value: 'net60', label: 'Net 60 days' },
  { value: 'net90', label: 'Net 90 days' },
];

// ==================== APPROVAL SETTINGS ====================

export type ApprovalApproverType = 'role' | 'user';
export type ApprovalDepartmentScope = 'all' | 'general' | 'it';
export type ApprovalConditionType = 'always' | 'amount_threshold';

export interface ApprovalLevel {
  id: string;
  level: number;
  name: string;
  approverType: ApprovalApproverType;
  approverRole?: string;
  approverUserId?: string;
  departmentScope: ApprovalDepartmentScope[];
  conditionType: ApprovalConditionType;
  amountThreshold?: number;
}

export interface ApprovalSettings {
  levels: ApprovalLevel[];
  escalationHours: number;
  autoEscalationEnabled: boolean;
  notifyOnDelegation: boolean;
  allowDelegation: boolean;
}

const DEFAULT_APPROVAL_SETTINGS: ApprovalSettings = {
  levels: [],
  escalationHours: 24,
  autoEscalationEnabled: true,
  notifyOnDelegation: true,
  allowDelegation: true,
};

export const ROLE_OPTIONS = [
  { value: 'manager', label: 'Manager' },
  { value: 'department_head', label: 'Department Head' },
  { value: 'director', label: 'Director' },
  { value: 'it_manager', label: 'IT Manager' },
  { value: 'finance', label: 'Finance' },
  { value: 'general_manager', label: 'General Manager' },
];

export const DEPARTMENT_OPTIONS = [
  { value: 'all', label: 'All Departments' },
  { value: 'general', label: 'Umum' },
  { value: 'it', label: 'IT' },
];

// ==================== GENERAL SETTINGS ====================

export type DateFormatPreference = 'dd/mm/yyyy' | 'mm/dd/yyyy';

export interface GeneralSettings {
  hospitalName: string;
  currency: string;
  dateFormat: DateFormatPreference;
  defaultLocationId?: string;
  timezone: string;
}

const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  hospitalName: 'Hospital',
  currency: 'IDR',
  dateFormat: 'dd/mm/yyyy',
  timezone: 'Asia/Jakarta',
};

export const DATE_FORMAT_OPTIONS = [
  { value: 'dd/mm/yyyy', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'mm/dd/yyyy', label: 'MM/DD/YYYY (12/31/2024)' },
];

// ==================== STORAGE KEYS ====================

const STORAGE_KEYS = {
  INVENTORY: 'inventory_settings',
  PURCHASE: 'purchase_settings',
  APPROVAL: 'approval_settings',
  GENERAL: 'general_settings',
};

// ==================== INVENTORY FUNCTIONS ====================

export function getInventorySettings(): InventorySettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.INVENTORY);
    if (stored) {
      return { ...DEFAULT_INVENTORY_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading inventory settings:', error);
  }
  return DEFAULT_INVENTORY_SETTINGS;
}

export function saveInventorySettings(settings: Partial<InventorySettings>): InventorySettings {
  const current = getInventorySettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(STORAGE_KEYS.INVENTORY, JSON.stringify(updated));
  return updated;
}

export function resetInventorySettings(): InventorySettings {
  localStorage.setItem(STORAGE_KEYS.INVENTORY, JSON.stringify(DEFAULT_INVENTORY_SETTINGS));
  return DEFAULT_INVENTORY_SETTINGS;
}

// ==================== PURCHASE FUNCTIONS ====================

export function getPurchaseSettings(): PurchaseSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PURCHASE);
    if (stored) {
      return { ...DEFAULT_PURCHASE_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading purchase settings:', error);
  }
  return DEFAULT_PURCHASE_SETTINGS;
}

export function savePurchaseSettings(settings: Partial<PurchaseSettings>): PurchaseSettings {
  const current = getPurchaseSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(STORAGE_KEYS.PURCHASE, JSON.stringify(updated));
  return updated;
}

export function resetPurchaseSettings(): PurchaseSettings {
  localStorage.setItem(STORAGE_KEYS.PURCHASE, JSON.stringify(DEFAULT_PURCHASE_SETTINGS));
  return DEFAULT_PURCHASE_SETTINGS;
}

// ==================== APPROVAL FUNCTIONS ====================

export function getApprovalSettings(): ApprovalSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.APPROVAL);
    if (stored) {
      return { ...DEFAULT_APPROVAL_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading approval settings:', error);
  }
  return DEFAULT_APPROVAL_SETTINGS;
}

export function saveApprovalSettings(settings: Partial<ApprovalSettings>): ApprovalSettings {
  const current = getApprovalSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(STORAGE_KEYS.APPROVAL, JSON.stringify(updated));
  return updated;
}

export function resetApprovalSettings(): ApprovalSettings {
  localStorage.setItem(STORAGE_KEYS.APPROVAL, JSON.stringify(DEFAULT_APPROVAL_SETTINGS));
  return DEFAULT_APPROVAL_SETTINGS;
}

export function addApprovalLevel(level: Omit<ApprovalLevel, 'id' | 'level'>): ApprovalSettings {
  const settings = getApprovalSettings();
  const newLevel: ApprovalLevel = {
    ...level,
    id: crypto.randomUUID(),
    level: settings.levels.length + 1,
  };
  return saveApprovalSettings({ levels: [...settings.levels, newLevel] });
}

export function updateApprovalLevel(id: string, updates: Partial<ApprovalLevel>): ApprovalSettings {
  const settings = getApprovalSettings();
  const levels = settings.levels.map((l) => (l.id === id ? { ...l, ...updates } : l));
  return saveApprovalSettings({ levels });
}

export function removeApprovalLevel(id: string): ApprovalSettings {
  const settings = getApprovalSettings();
  const levels = settings.levels
    .filter((l) => l.id !== id)
    .map((l, index) => ({ ...l, level: index + 1 }));
  return saveApprovalSettings({ levels });
}

export function reorderApprovalLevels(fromIndex: number, toIndex: number): ApprovalSettings {
  const settings = getApprovalSettings();
  const levels = [...settings.levels];
  const [moved] = levels.splice(fromIndex, 1);
  levels.splice(toIndex, 0, moved);
  const reordered = levels.map((l, index) => ({ ...l, level: index + 1 }));
  return saveApprovalSettings({ levels: reordered });
}

// ==================== GENERAL FUNCTIONS ====================

export function getGeneralSettings(): GeneralSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.GENERAL);
    if (stored) {
      return { ...DEFAULT_GENERAL_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading general settings:', error);
  }
  return DEFAULT_GENERAL_SETTINGS;
}

export function saveGeneralSettings(settings: Partial<GeneralSettings>): GeneralSettings {
  const current = getGeneralSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(STORAGE_KEYS.GENERAL, JSON.stringify(updated));
  return updated;
}

export function resetGeneralSettings(): GeneralSettings {
  localStorage.setItem(STORAGE_KEYS.GENERAL, JSON.stringify(DEFAULT_GENERAL_SETTINGS));
  return DEFAULT_GENERAL_SETTINGS;
}

// ==================== UTILITY FUNCTIONS ====================

export function roundWithSettings(value: number, settings?: InventorySettings): number {
  const s = settings || getInventorySettings();
  
  if (s.decimalRounding === 'none') {
    return value;
  }
  
  if (s.decimalDigits === 0) {
    return Math.round(value);
  }
  
  const factor = Math.pow(10, s.decimalDigits);
  
  switch (s.decimalRounding) {
    case 'up':
      return Math.ceil(value * factor) / factor;
    case 'down':
      return Math.floor(value * factor) / factor;
    case 'nearest':
    default:
      return Math.round(value * factor) / factor;
  }
}

export function shouldAlertLowStock(
  currentQty: number,
  minStock: number,
  alertPercent: number = getInventorySettings().minimumStockAlertPercent
): boolean {
  if (minStock <= 0) return false;
  const percentRemaining = (currentQty / minStock) * 100;
  return percentRemaining <= alertPercent;
}

export function isStockValid(currentQty: number, settings?: InventorySettings): { valid: boolean; reason?: string } {
  const s = settings || getInventorySettings();
  
  if (currentQty >= 0) {
    return { valid: true };
  }
  
  if (s.allowNegativeStock) {
    return { valid: true };
  }
  
  return { valid: false, reason: 'Negative stock not allowed' };
}
